// front/src/preview-mode/graph/seed.ts
export const PREVIEW_SEED = 0xce1103;

export interface Rng {
  next: () => number;
  int: (n: number) => number;
  pick: <T>(arr: readonly T[]) => T;
  weightedPick: <T>(items: readonly { value: T; weight: number }[]) => T;
  uuid: () => string;
}

// Mulberry32 PRNG: small, fast, deterministic.
export function createRng(seed: number): Rng {
  let state = seed >>> 0;

  const next = (): number => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const int = (n: number): number => Math.floor(next() * n);

  const pick = <T>(arr: readonly T[]): T => {
    if (arr.length === 0) throw new Error("pick called with empty array");
    return arr[int(arr.length)];
  };

  const weightedPick = <T>(
    items: readonly { value: T; weight: number }[],
  ): T => {
    if (items.length === 0)
      throw new Error("weightedPick called with empty array");
    const total = items.reduce((sum, it) => sum + it.weight, 0);
    let roll = next() * total;
    for (const it of items) {
      roll -= it.weight;
      if (roll <= 0) return it.value;
    }
    return items[items.length - 1].value;
  };

  const hex = (bytes: number): string => {
    let out = "";
    for (let i = 0; i < bytes; i++) {
      out += int(256).toString(16).padStart(2, "0");
    }
    return out;
  };

  const uuid = (): string => {
    // v4: 8-4-4-4-12, version nibble 4, variant bits 10xx
    const hex1 = hex(4);
    const hex2 = hex(2);
    const hex3Raw = int(0x1000).toString(16).padStart(3, "0");
    const hex3 = `4${hex3Raw}`;
    const variantFirst = (8 + int(4)).toString(16);
    const hex4 = `${variantFirst}${int(0x1000).toString(16).padStart(3, "0")}`;
    const hex5 = hex(6);
    return `${hex1}-${hex2}-${hex3}-${hex4}-${hex5}`;
  };

  return { next, int, pick, weightedPick, uuid };
}
